<template>
    <kgr-generic-modal
        v-if="showDialog"
        :value="showDialog"
        :headline="tHeadline"
        :width-in-px="600"
        scrollable
        @input="closeDialog()"
    >
        <div class="kgr-grid__cell">
            <input
                v-model.trim="title"
                :placeholder="tTitle"
                class="kgr-input"
                :maxlength="ChatEntry.NAME_MAX_LENGTH"
            />
        </div>
        <template v-if="!isEditMode">
            <div
                class="kgr-grid__cell"
                style="margin-top: 20px;"
            >
                <input
                    ref="searchInput"
                    v-model="search"
                    type="search"
                    :placeholder="tSearchInfo"
                    class="kgr-input"
                    @input="onSearch"
                />
            </div>
            <div
                class="kgr-grid__cell"
                style="margin-top: 20px;"
            >
                <table
                    class="kgr-table kgr-table--striped"
                    style="width: 100%;"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ tChild }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ tParentUser }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="3">
                                <kgr-loading inline />
                            </td>
                        </tr>
                        <tr v-else-if="!loading && orderedRelationships.length === 0">
                            <td colspan="3">
                                {{ $t('Kein Suchergebnis') }}
                            </td>
                        </tr>
                        <tr
                            v-else
                            v-for="relationship in orderedRelationships"
                            :key="relationship.id"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                {{ relationship.child.fullName }}
                            </td>
                            <td class="kgr-table__cell">
                                {{ relationship.parentUser.fullName }}
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-checkbox
                                    :checked="selectedRelationships.includes(relationship)"
                                    @change="addOrRemoveRelationship(relationship)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <template #footer>
            <kgr-button
                class="kgr-col kgr-button--outline float-left"
                @click="closeDialog"
            >
                {{ tCancel }}
            </kgr-button>
            <kgr-button
                class="kgr-col"
                :disabled="saveButtonDisabled"
                @click="saveChat"
            >
                {{ tSaveAndClose }}
            </kgr-button>
        </template>
    </kgr-generic-modal>
</template>

<script>
import ChatEntry from '../../js/dto/ChatEntry.js';
import ChatRepository from '../../js/repository/ChatRepository.js';
import Checkbox from '../common/form/checkbox/Checkbox.vue';
import ChildParentUserRelationshipRepository from '../../js/repository/kindergarten/ChildParentUserRelationshipRepository.js';
import GenericModal from '../common/modal/GenericModal.vue';
import KgrButton from '../common/form/button/KgrButton.vue';
import Loading from '../common/Loading.vue';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-checkbox': Checkbox,
        'kgr-generic-modal': GenericModal,
        'kgr-loading': Loading,
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true,
        },
        institutionId: {
            type: Number,
            required: true,
        },
        institutionKey: {
            type: String,
            required: true,
        },
        chat: {
            type: [ChatEntry, null],
            required: false,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            title: '',
            search: '',
            searchTimer: null,
            relationships: [],
            selectedRelationships: [],
            orderedRelationships: false,
        };
    },
    computed: {
        ChatEntry() {
            return ChatEntry;
        },
        isKindergartenChat() {
            return this.institutionKey === 'kindergarten';
        },
        saveButtonDisabled() {
            if (this.isEditMode) {
                return false;
            }
            return this.selectedRelationships.length === 0;
        },
        isEditMode() {
            return this.chat !== null;
        },
        tHeadline() {
            return this.chat === null ? this.$t('Neuen Chat anlegen') : this.$t('Chat bearbeiten');
        },
        tCancel() {
            return this.$t('cancel');
        },
        tSaveAndClose() {
            return this.chat === null ? this.$t('Chat anlegen und schließen') : this.$t('Chat speichern und schließen');
        },
        tTitle() {
            return this.$t('Name des Chats (optional)');
        },
        tSearchInfo() {
            return this.$t('Suche nach Kindern und Bezugspersonen');
        },
        tChild() {
            return this.$t('Kind');
        },
        tParentUser() {
            return this.$t('Bezugsperson');
        },
    },
    watch: {
        chat() {
            this.title = this.chat ? this.chat.name : '';
        },
    },
    methods: {
        closeDialog() {
            this.search = '';
            this.relationships = [];
            this.selectedRelationships = [];
            this.orderedRelationships = [];
            this.$emit('closeDialog');
        },
        saveChat() {
            this.isEditMode ? this.updateChat() : this.createChat();
        },
        createChat() {
            const title = this.title !== '' ? this.title : null;
            if (this.isKindergartenChat) {
                // get distinct child- and user-ids
                const childIds = [...new Set(this.selectedRelationships.map((relationship) => relationship.child.id))];
                const userIds = [...new Set(this.selectedRelationships.map((relationship) => relationship.parentUser.id))];
                ChatRepository.createForKindergartenAndChildAndUsers(this.institutionId, childIds, userIds, title)
                    .then((response) => {
                        this.$notify.success('Der Chat wurde angelegt');
                        const newChatId = response.headers['x-identifier'];
                        this.$emit('newChat', newChatId);
                        this.closeDialog();
                    })
                    .catch(() => this.$notify.error('chat.create.error'));
            }
        },
        updateChat() {
            if (this.isKindergartenChat && this.chat) {
                const title = this.title !== null && this.title !== '' ? this.title : null;
                ChatRepository.update(this.chat.id, title)
                    .then(() => {
                        this.$notify.success('Der Chat wurde gespeichert');
                        this.$emit('updateName', title);
                        this.closeDialog();
                    })
                    .catch(() => this.$notify.error('Der Chat konnte nicht gespeichert werden'));
            }
        },
        onSearch(event) {
            if (['Escape', 'ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
                return;
            }
            if (this.search.trim().length < 2) {
                return;
            }
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.doSearch();
            }, 600);
        },
        doSearch() {
            const search = this.search.trim();
            if (search.length < 2) {
                return;
            }
            this.loading = true;
            if (this.isKindergartenChat) {
                ChildParentUserRelationshipRepository.findForChatsByKindergartenAndSearch(this.institutionId, search)
                    .then((response) => {
                        this.relationships = response;
                        this.reorderRelationships();
                    })
                    .catch(() => this.$notify.error('error.loadingData'))
                    .finally(() => this.loading = false);
            }
        },
        addOrRemoveRelationship(relationship) {
            if (!this.selectedRelationships.includes(relationship)) {
                this.selectedRelationships.push(relationship);
                this.search = '';
            } else {
                const index = this.selectedRelationships.indexOf(relationship);
                if (index !== -1) {
                    this.selectedRelationships.splice(index, 1);
                }
            }
            this.$refs.searchInput.focus();
        },
        reorderRelationships() {
            const selectedRelationshipIds = this.selectedRelationships.map((r) => r.id);
            this.orderedRelationships = [
                ...this.relationships.filter((relationship) => !selectedRelationshipIds.includes(relationship.id)),
                ...this.selectedRelationships,
            ];
        },
    },
};
</script>

<style scoped lang="scss"></style>
